import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import {
  message, Radio, Space, Typography
} from 'antd';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { actions, selectors } from 'farmx-redux-core';
import { coordinationApi } from 'farmx-api';
import { LoadingOutlined } from '@ant-design/icons';
import { isMobile } from '../../../utils/detectDevice';

interface AutonomousIrrigationBlockProps {
  block: {
    id: string | number;
    name: string;
  };
}

const RenderAutonomousIrrigationBlock: React.FC<AutonomousIrrigationBlockProps> = (props: any) => {
  const { block } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const selected = useRef<string | undefined>('');

  const configType = 'irrigation';
  const irrigationOptions = [
    { label: t('VWC Based Autonomous Irrigation'), value: 'autonomous_irrigation' },
    { label: t('Disable'), value: 'disable' },
  ];
  

  const irrigationConfig = useSelector((state) => selectors
    .selectIrrigationConfigByBlockId(state, block.id)) || {};

  const coordinationLoading = useSelector((state) => selectors.selectCoordinationLoading(state));

  const onChange = useCallback(async (e) => {
    setIsLoading(true);
    try {
      // This log will be removed
      console.log('ON CHANGE :: RenderAutonomousIrrigationBlock', {
        blockId: block.id,
        actionType: configType,
        actionSubType: e.target.value,
        autoMode: e.target.value !== 'disable'
      });

      // This will be uncommented
      await coordinationApi.updateSensorCoordination({
        blockId: block.id,
        actionType: configType,
        actionSubType: e.target.value,
        autoMode: e.target.value !== 'disable'
      });

      // // Update redux store if API is called successfully
      await dispatch(actions.updateConfigField({
        blockId: block.id,
        configType,
        field: e.target.value,
      }));

      setIsLoading(false);
    } catch(e){
      message.error(t('Update failed. Please make sure rules are configured for the Block.'));
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, dispatch]);

  useEffect(() => {
    if(!isEmpty(irrigationConfig)){
      const isDisabled = Object.keys(irrigationConfig).every((key) => !irrigationConfig[key]);
      // when isDisabled is false, set selected to the key of the irrigationConfig
      // object where the value is true
      selected.current = isDisabled ? 'disable' : Object.keys(irrigationConfig)
        .find((key) => irrigationConfig[key] === true);
    }
  }, [block.id, irrigationConfig]);

  return (
    <div className="render-block-settings">
      <Typography.Text>
        {block.name}
        {(isLoading || coordinationLoading) && <LoadingOutlined className="margin-left-10" />}
      </Typography.Text>
      <Radio.Group disabled={isLoading} onChange={onChange} value={selected.current}>
        <Space direction={irrigationOptions.length > 2 && isMobile ? 'vertical' : 'horizontal'}>
          {
          irrigationOptions.map((v) => (
            <Radio key={v.value} value={v.value}>{v.label}</Radio>
          ))
        }
        </Space>
      </Radio.Group>
    </div>
  );
};

export default RenderAutonomousIrrigationBlock;
